// Empire Apps Chassis · identity-zkp · ABHA ZKP predicates
// Wraps the ABDM PHR stub validator. Predicates useful for clinic onboarding:
//   - abha_active = true
//   - age_band ∈ {child, teen, young_adult, adult, senior}
//   - gender_code ∈ {M, F, T}
//   - state_code (derived from optional locator)
// The full ABHA number is never disclosed; clinics get a deterministic
// pseudonym hash instead.

import type {
  Predicate,
  AttributeValue,
  Ed25519PrivateKey,
  Ed25519PublicKey,
} from './types.ts';
import { bbsSign, type BBSSignedCredential } from './bbs-plus.ts';
import { deriveBundle, type DisclosureBundle } from './selective-disclosure.ts';
import { commit, newBlindingFactor, toHex } from './commitment.ts';

export interface AbhaInfo {
  readonly raw: string;
  readonly shape: '14digit' | 'address';
  readonly normalised: string;
  readonly status: 'valid' | 'invalid' | 'structural';
  readonly ageYears?: number;
  readonly genderCode?: 'M' | 'F' | 'T';
  readonly stateCode?: string;
}

export interface AbhaZkpAttributes {
  readonly abha_active: boolean;
  readonly age_band: 'child' | 'teen' | 'young_adult' | 'adult' | 'senior' | 'unknown';
  readonly age_gte_18: boolean;
  readonly age_gte_60: boolean;
  readonly gender_code: string;
  readonly state_code: string;
  readonly abha_pseudonym: string;
}

export function ageBand(years: number | undefined): AbhaZkpAttributes['age_band'] {
  if (years === undefined) return 'unknown';
  if (years < 13) return 'child';
  if (years < 20) return 'teen';
  if (years < 40) return 'young_adult';
  if (years < 60) return 'adult';
  return 'senior';
}

export async function abhaPseudonym(abhaRaw: string, blindingHex?: string): Promise<string> {
  const bf = blindingHex ? new Uint8Array(Array.from(blindingHex.matchAll(/.{2}/g), m => parseInt(m[0] as string, 16))) : newBlindingFactor();
  const c = await commit('abha_id', abhaRaw, bf as Uint8Array & { readonly __zkpBrand: 'BlindingFactor' });
  return c.slice(0, 16);
}

export async function abhaToZkpAttributes(info: AbhaInfo): Promise<AbhaZkpAttributes> {
  return {
    abha_active: info.status === 'valid',
    age_band: ageBand(info.ageYears),
    age_gte_18: (info.ageYears ?? 0) >= 18,
    age_gte_60: (info.ageYears ?? 0) >= 60,
    gender_code: info.genderCode ?? 'X',
    state_code: info.stateCode ?? 'XX',
    abha_pseudonym: await abhaPseudonym(info.normalised),
  };
}

export async function abhaSign(
  info: AbhaInfo,
  issuerSk: Ed25519PrivateKey,
  issuerPk: Ed25519PublicKey,
): Promise<BBSSignedCredential> {
  const attrs = await abhaToZkpAttributes(info);
  const flat: Record<string, AttributeValue> = {
    abha_active: attrs.abha_active,
    age_band: attrs.age_band,
    age_gte_18: attrs.age_gte_18,
    age_gte_60: attrs.age_gte_60,
    gender_code: attrs.gender_code,
    state_code: attrs.state_code,
    abha_pseudonym: attrs.abha_pseudonym,
  };
  return bbsSign(flat, issuerSk, issuerPk);
}

export const ABHA_PREDICATE_LIBRARY = {
  active: { attribute: 'abha_active', op: 'eq', target: true } as Predicate,
  adult: { attribute: 'age_gte_18', op: 'eq', target: true } as Predicate,
  senior: { attribute: 'age_gte_60', op: 'eq', target: true } as Predicate,
  bandIn: (bands: readonly string[]): Predicate => ({ attribute: 'age_band', op: 'in', target: bands }),
  genderEq: (g: 'M' | 'F' | 'T'): Predicate => ({ attribute: 'gender_code', op: 'eq', target: g }),
  pseudonymReveal: { attribute: 'abha_pseudonym', op: 'reveal', target: '' } as Predicate,
} as const;

export async function abhaDeriveBundle(
  cred: BBSSignedCredential,
  predicates: readonly Predicate[],
): Promise<DisclosureBundle> {
  return deriveBundle(cred, predicates);
}
